/**
 * Страница ручных рассылок (Manual Broadcasts)
 * Дата: 26.02.2026 — PLAN Manual Broadcasts, Итерация 5
 * Дата: 17.03.2026 — Per-broadcast launch lock через CheckboxTable вместо tenant-global lock
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw } from 'lucide-react';
import { MainLayout } from '@/components/layout/MainLayout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  EmptyState,
  LoadingState,
  FilterSelect,
  TenantSelector,
  CheckboxTable,
  type Column,
} from '@/components/ui';
import { useManualBroadcasts } from '@/hooks/useManualBroadcasts';
import { usePermissions } from '@/hooks/usePermissions';
import { useTenantScope } from '@/hooks/useTenantScope';
import { useToast } from '@/hooks/useToast';
import { BroadcastActions } from '@/components/features/manual-broadcasts/BroadcastActions';
import { BroadcastStats } from '@/components/features/manual-broadcasts/BroadcastStats';
import { manualBroadcastsApi, type ManualBroadcast } from '@/api/manualBroadcasts';
import { formatDate } from '@/utils/formatters';
import { getChannelLabel } from '@/config/channels';

const STATUS_OPTIONS = [
  { value: '', label: 'Все статусы' },
  { value: 'draft', label: 'Черновик' },
  { value: 'running', label: 'Выполняется' },
  { value: 'completed', label: 'Завершена' },
  { value: 'failed', label: 'Ошибка' },
  { value: 'cancelled', label: 'Отменена' },
];

function getSegmentTypeLabel(type: string): string {
  if (type === 'regular') return 'Обычный';
  if (type === 'dynamic') return 'Динамический';
  if (type === 'lk') return 'ЛК';
  return type;
}

function renderBroadcastStatsCell(b: {
  status: string;
  total_recipients: number;
  sent_count: number;
  failed_count: number;
  skipped_count: number;
  selected_phones?: string[];
}) {
  if (b.status === 'draft') {
    const totalInSegment = b.total_recipients > 0 ? b.total_recipients : (b.selected_phones?.length ?? 0);
    const selectedForSend = (b.selected_phones?.length ?? 0) > 0 ? (b.selected_phones?.length ?? 0) : totalInSegment;
    return (
      <div className="space-y-1 text-xs">
        <div className="text-neutral-600">
          Всего: <span className="font-medium text-neutral-900">{totalInSegment}</span>
        </div>
        <div className="text-primary-700">
          Выбрано к отправке: <span className="font-semibold">{selectedForSend}</span>
        </div>
      </div>
    );
  }

  return (
    <BroadcastStats
      total={b.total_recipients}
      sent={b.sent_count}
      failed={b.failed_count}
      skipped={b.skipped_count}
      compact
    />
  );
}

function renderLaunchStateCell(broadcast: ManualBroadcast) {
  if (broadcast.status !== 'draft') {
    return <span className="text-xs text-neutral-400">Только для черновиков</span>;
  }

  if (broadcast.launch_locked) {
    return (
      <span className="inline-flex rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-700">
        Запуск заблокирован
      </span>
    );
  }

  return (
    <span className="inline-flex rounded-full bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-700">
      Запуск разрешён
    </span>
  );
}

function BroadcastRowActions({
  broadcast,
  canWrite,
  actionLoading,
  onOpenRecipients,
  onLaunch,
  onCancel,
  onDelete,
}: {
  broadcast: ManualBroadcast;
  canWrite: boolean;
  actionLoading: boolean;
  onOpenRecipients: (broadcast: ManualBroadcast) => void;
  onLaunch: (id: string) => void;
  onCancel: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => onOpenRecipients(broadcast)}
        className="text-primary-600 hover:underline text-sm"
      >
        Получатели
      </button>
      <BroadcastActions
        broadcast={broadcast}
        onLaunch={onLaunch}
        onCancel={onCancel}
        onDelete={onDelete}
        canWrite={canWrite}
        launchBlocked={Boolean(broadcast.launch_locked)}
        loading={actionLoading}
      />
    </div>
  );
}

export function ManualBroadcastsPage() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { can } = usePermissions();
  const { tenantId, isSuperAdmin, selectedTenantId, setSelectedTenant, requireTenantSelection } = useTenantScope();

  const [statusFilter, setStatusFilter] = useState('');
  const [offset] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [launchLockLoading, setLaunchLockLoading] = useState(false);

  const { broadcasts, total, loading, error, load, launch, cancel, remove } = useManualBroadcasts({
    tenantId,
    status: statusFilter || undefined,
    limit: 20,
    offset,
  });

  const canWrite = can('campaigns:write');

  useEffect(() => {
    setSelectedIds(new Set());
  }, [tenantId, statusFilter]);

  useEffect(() => {
    const currentIds = new Set(broadcasts.filter((item) => item.status === 'draft').map((item) => item.id));
    setSelectedIds((prev) => {
      const next = new Set<string>();
      prev.forEach((id) => {
        if (currentIds.has(id)) {
          next.add(id);
        }
      });
      return next;
    });
  }, [broadcasts]);

  const selectedDrafts = useMemo(
    () => broadcasts.filter((broadcast) => selectedIds.has(broadcast.id) && broadcast.status === 'draft'),
    [broadcasts, selectedIds],
  );

  const selectedUnlockedCount = selectedDrafts.filter((broadcast) => !broadcast.launch_locked).length;
  const selectedLockedCount = selectedDrafts.filter((broadcast) => broadcast.launch_locked).length;

  const handleLaunch = async (id: string) => {
    try {
      setActionLoading(true);
      await launch(id);
      showSuccess('Рассылка запущена');
    } catch {
      showError('Не удалось запустить рассылку');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async (id: string) => {
    try {
      setActionLoading(true);
      await cancel(id);
      showSuccess('Рассылка отменена');
    } catch {
      showError('Не удалось отменить рассылку');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Удалить рассылку?')) return;
    try {
      setActionLoading(true);
      await remove(id);
      showSuccess('Рассылка удалена');
    } catch {
      showError('Не удалось удалить рассылку');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkLaunchLock = async (launchLocked: boolean) => {
    if (!tenantId || selectedDrafts.length === 0) return;
    try {
      setLaunchLockLoading(true);
      const response = await manualBroadcastsApi.setLaunchLocked(
        selectedDrafts.map((broadcast) => broadcast.id),
        launchLocked,
        tenantId,
      );
      showSuccess(response.message);
      setSelectedIds(new Set());
      await load();
    } catch (e: any) {
      showError(e?.response?.data?.error || 'Не удалось обновить блокировку запуска');
    } finally {
      setLaunchLockLoading(false);
    }
  };

  const openRecipients = (broadcast: ManualBroadcast) => {
    navigate(`/manual-broadcasts/${broadcast.id}/recipients?tenant_id=${encodeURIComponent(broadcast.tenant_id)}`);
  };

  const checkboxColumns: Column<ManualBroadcast>[] = [
    {
      key: 'name',
      header: 'Название',
      render: (broadcast) => <div className="font-medium">{broadcast.name}</div>,
    },
    {
      key: 'segment',
      header: 'Сегмент',
      render: (broadcast) => (
        <div>
          {broadcast.segment_name}
          <span className="ml-1 text-xs text-neutral-500">({getSegmentTypeLabel(broadcast.segment_type)})</span>
        </div>
      ),
    },
    {
      key: 'template',
      header: 'Шаблон',
      render: (broadcast) => (
        <div>
          <div className="font-medium">{broadcast.template_name}</div>
          <div className="text-xs text-neutral-500">
            {broadcast.channel_override ? getChannelLabel(broadcast.channel_override) : 'Авто (FallbackEngine)'}
          </div>
        </div>
      ),
    },
    {
      key: 'launch_state',
      header: 'Запуск',
      render: (broadcast) => renderLaunchStateCell(broadcast),
    },
    {
      key: 'stats',
      header: 'Статистика',
      render: (broadcast) => renderBroadcastStatsCell(broadcast),
    },
    {
      key: 'created_at',
      header: 'Создана',
      render: (broadcast) => formatDate(broadcast.created_at),
    },
    {
      key: 'actions',
      header: 'Действия',
      render: (broadcast) => (
        <BroadcastRowActions
          broadcast={broadcast}
          canWrite={canWrite}
          actionLoading={actionLoading}
          onOpenRecipients={openRecipients}
          onLaunch={handleLaunch}
          onCancel={handleCancel}
          onDelete={handleDelete}
        />
      ),
      className: 'min-w-[340px]',
    },
  ];

  return (
    <MainLayout
      headerTitle="Ручные рассылки"
      headerActions={
        <>
          {canWrite && (
            <Button variant="primary" onClick={() => navigate('/manual-broadcasts/create')} testId="btn-create-broadcast">
              <Plus className="w-4 h-4 mr-2" />
              Создать рассылку
            </Button>
          )}
          <Button variant="secondary" onClick={() => load()} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Обновить
          </Button>
        </>
      }
    >
      <div className="p-6 space-y-4">
        <div className="flex gap-4">
          {isSuperAdmin && (
            <TenantSelector
              value={selectedTenantId ?? ''}
              onChange={setSelectedTenant}
            />
          )}
          <div className="w-48">
            <FilterSelect
              value={statusFilter}
              onChange={setStatusFilter}
              options={STATUS_OPTIONS}
              placeholder="Все статусы"
            />
          </div>
        </div>

        {isSuperAdmin && tenantId && (
          <Card>
            <CardHeader>
              <CardTitle>Управление запуском выбранных рассылок</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="text-sm text-neutral-600">
                Выберите черновики чекбоксами в таблице ниже. Можно оставить одну кампанию разблокированной для коллеги, а остальные временно заморозить.
              </div>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="text-neutral-600">Выбрано черновиков: {selectedDrafts.length}</span>
                <span className="text-emerald-700">Разрешены: {selectedUnlockedCount}</span>
                <span className="text-red-700">Заблокированы: {selectedLockedCount}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  onClick={() => void handleBulkLaunchLock(true)}
                  disabled={launchLockLoading || selectedDrafts.length === 0 || selectedUnlockedCount === 0}
                >
                  {launchLockLoading ? 'Сохранение…' : 'Заблокировать запуск выбранных'}
                </Button>
                <Button
                  variant="primary"
                  onClick={() => void handleBulkLaunchLock(false)}
                  disabled={launchLockLoading || selectedDrafts.length === 0 || selectedLockedCount === 0}
                >
                  {launchLockLoading ? 'Сохранение…' : 'Разблокировать запуск выбранных'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Список рассылок ({total})</CardTitle>
          </CardHeader>
          <CardContent>
            {requireTenantSelection ? (
              <EmptyState
                type="noData"
                title="Выберите клинику"
                description="Выберите клинику для просмотра ручных рассылок"
              />
            ) : loading ? (
              <LoadingState />
            ) : error ? (
              <div className="text-red-600 py-4">{error}</div>
            ) : !broadcasts || broadcasts.length === 0 ? (
              <EmptyState
                type="noData"
                title="Нет рассылок"
                description="Создайте первую ручную рассылку"
                actionText="Создать рассылку"
                onAction={() => canWrite && navigate('/manual-broadcasts/create')}
              />
            ) : isSuperAdmin ? (
              <CheckboxTable
                data={broadcasts}
                columns={checkboxColumns}
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
                getRowId={(broadcast) => broadcast.id}
                isRowDisabled={(broadcast) => broadcast.status !== 'draft'}
                testId="manual-broadcasts-checkbox-table"
              />
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Название</TableHeader>
                    <TableHeader>Сегмент</TableHeader>
                    <TableHeader>Шаблон</TableHeader>
                    <TableHeader>Запуск</TableHeader>
                    <TableHeader>Статистика</TableHeader>
                    <TableHeader>Создана</TableHeader>
                    <TableHeader>Действия</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {broadcasts.map((broadcast) => (
                    <TableRow key={broadcast.id}>
                      <TableCell className="font-medium">{broadcast.name}</TableCell>
                      <TableCell>
                        {broadcast.segment_name}
                        <span className="ml-1 text-xs text-neutral-500">({getSegmentTypeLabel(broadcast.segment_type)})</span>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{broadcast.template_name}</div>
                        <div className="text-xs text-neutral-500">
                          {broadcast.channel_override ? getChannelLabel(broadcast.channel_override) : 'Авто (FallbackEngine)'}
                        </div>
                      </TableCell>
                      <TableCell>{renderLaunchStateCell(broadcast)}</TableCell>
                      <TableCell>{renderBroadcastStatsCell(broadcast)}</TableCell>
                      <TableCell>{formatDate(broadcast.created_at)}</TableCell>
                      <TableCell>
                        <BroadcastRowActions
                          broadcast={broadcast}
                          canWrite={canWrite}
                          actionLoading={actionLoading}
                          onOpenRecipients={openRecipients}
                          onLaunch={handleLaunch}
                          onCancel={handleCancel}
                          onDelete={handleDelete}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}
